/**
 * @boli/core - KYC Service
 *
 * Platform-agnostic KYC (Know Your Customer) verification service for Boli Live.
 * This service is used by BOTH Web (Phase 1) and Mobile (Phase 2) with ZERO changes.
 *
 * Key Features:
 * - Check current KYC status and bid limits
 * - Submit CNIC documents for verification
 * - Track verification progress
 *
 * Flow:
 * 1. User uploads CNIC front/back images
 * 2. Submits for review with requested tier
 * 3. Admin reviews and approves/rejects
 * 4. User's bid limit updated based on tier
 */

import type {
  KYCSubmission,
  KYCStatusResponse,
  KYCTier,
  ApiResponse,
  KYC_CONFIG,
} from '@boli/types';

// ============================================================================
// INTERFACES
// ============================================================================

export interface IKYCService {
  /**
   * Get current KYC status
   */
  getKYCStatus(): Promise<KYCStatusResponse | null>;

  /**
   * Submit KYC documents for verification
   */
  submitKYC(data: KYCSubmitData): Promise<ApiResponse<KYCSubmission>>;

  /**
   * Get bid limit for a tier
   */
  getBidLimit(tier: KYCTier | 'none'): number;

  /**
   * Validate CNIC number format
   */
  validateCNIC(cnic: string): { valid: boolean; message?: string };
}

export interface KYCSubmitData {
  cnicNumber: string;
  cnicFrontFile: File | Blob;
  cnicBackFile: File | Blob;
  fullName: string;
  dateOfBirth: string;
  addressProofFile?: File | Blob;
  requestedTier: KYCTier;
}

export interface KYCServiceConfig {
  apiBaseUrl: string;
  tier1BidLimit: number;
  tier2BidLimit: number;
  defaultBidLimit: number;
}

// ============================================================================
// DEFAULT CONFIG
// ============================================================================

export const DEFAULT_KYC_CONFIG: KYCServiceConfig = {
  apiBaseUrl: '',
  tier1BidLimit: 50000, // PKR
  tier2BidLimit: 500000, // PKR
  defaultBidLimit: 50000, // PKR
};

// ============================================================================
// KYC SERVICE IMPLEMENTATION
// ============================================================================

export class KYCService implements IKYCService {
  private config: KYCServiceConfig;
  private fetchAdapter: (url: string, options?: RequestInit) => Promise<Response>;
  private getAuthToken: () => Promise<string | null>;

  constructor(
    config: Partial<KYCServiceConfig>,
    fetchAdapter: (url: string, options?: RequestInit) => Promise<Response>,
    getAuthToken: () => Promise<string | null>
  ) {
    this.config = { ...DEFAULT_KYC_CONFIG, ...config };
    this.fetchAdapter = fetchAdapter;
    this.getAuthToken = getAuthToken;
  }

  /**
   * Get authorization headers
   */
  private async getHeaders(): Promise<HeadersInit> {
    const token = await this.getAuthToken();
    return {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  }

  /**
   * Get current KYC status
   */
  async getKYCStatus(): Promise<KYCStatusResponse | null> {
    try {
      const response = await this.fetchAdapter(
        `${this.config.apiBaseUrl}/api/users/kyc-status`,
        { headers: await this.getHeaders() }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch KYC status');
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to get KYC status:', error);
      return null;
    }
  }

  /**
   * Submit KYC documents for verification
   */
  async submitKYC(data: KYCSubmitData): Promise<ApiResponse<KYCSubmission>> {
    try {
      // Validate CNIC format
      const cnicValidation = this.validateCNIC(data.cnicNumber);
      if (!cnicValidation.valid) {
        return {
          success: false,
          error: {
            code: 'INVALID_CNIC',
            message: cnicValidation.message || 'Invalid CNIC format',
          },
        };
      }

      // Validate date of birth
      const dob = new Date(data.dateOfBirth);
      const today = new Date();
      const age = Math.floor((today.getTime() - dob.getTime()) / (365.25 * 24 * 60 * 60 * 1000));
      if (age < 18) {
        return {
          success: false,
          error: {
            code: 'UNDERAGE',
            message: 'You must be at least 18 years old',
          },
        };
      }

      // Create form data for file upload
      const formData = new FormData();
      formData.append('cnicNumber', data.cnicNumber);
      formData.append('cnicFront', data.cnicFrontFile);
      formData.append('cnicBack', data.cnicBackFile);
      formData.append('fullName', data.fullName);
      formData.append('dateOfBirth', data.dateOfBirth);
      formData.append('requestedTier', data.requestedTier);
      if (data.addressProofFile) {
        formData.append('addressProof', data.addressProofFile);
      }

      const token = await this.getAuthToken();
      const response = await this.fetchAdapter(
        `${this.config.apiBaseUrl}/api/users/kyc`,
        {
          method: 'POST',
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: formData,
        }
      );

      const responseData = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: {
            code: responseData.code || 'SUBMIT_FAILED',
            message: responseData.message || 'Failed to submit KYC documents',
          },
        };
      }

      return { success: true, data: responseData.submission };
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Network error',
        },
      };
    }
  }

  /**
   * Get bid limit for a tier
   */
  getBidLimit(tier: KYCTier | 'none'): number {
    switch (tier) {
      case 'tier2':
        return this.config.tier2BidLimit;
      case 'tier1':
        return this.config.tier1BidLimit;
      default:
        return this.config.defaultBidLimit;
    }
  }

  /**
   * Validate CNIC number format
   * Format: XXXXX-XXXXXXX-X (13 digits with dashes)
   */
  validateCNIC(cnic: string): { valid: boolean; message?: string } {
    // Remove any spaces
    const cleaned = cnic.replace(/\s/g, '');

    // Check format with dashes: XXXXX-XXXXXXX-X
    const withDashesRegex = /^\d{5}-\d{7}-\d{1}$/;

    // Check format without dashes: 13 digits
    const withoutDashesRegex = /^\d{13}$/;

    if (withDashesRegex.test(cleaned) || withoutDashesRegex.test(cleaned)) {
      return { valid: true };
    }

    return {
      valid: false,
      message: 'CNIC must be 13 digits in format XXXXX-XXXXXXX-X',
    };
  }

  /**
   * Format CNIC for display (masked)
   */
  maskCNIC(cnic: string): string {
    const cleaned = cnic.replace(/[-\s]/g, '');
    if (cleaned.length !== 13) return cnic;
    return `${cleaned.slice(0, 5)}-XXXXXXX-${cleaned.slice(-1)}`;
  }

  /**
   * Format CNIC with dashes
   */
  formatCNIC(cnic: string): string {
    const cleaned = cnic.replace(/[-\s]/g, '');
    if (cleaned.length !== 13) return cnic;
    return `${cleaned.slice(0, 5)}-${cleaned.slice(5, 12)}-${cleaned.slice(-1)}`;
  }

  /**
   * Get tier display name
   */
  getTierDisplayName(tier: KYCTier | 'none'): string {
    switch (tier) {
      case 'tier2':
        return 'Tier 2 (Verified+)';
      case 'tier1':
        return 'Tier 1 (Basic)';
      default:
        return 'Unverified';
    }
  }

  /**
   * Get submission status display
   */
  getStatusDisplay(status: string): { label: string; color: string } {
    switch (status) {
      case 'pending':
        return { label: 'Under Review', color: 'yellow' };
      case 'approved':
        return { label: 'Verified', color: 'green' };
      case 'rejected':
        return { label: 'Rejected', color: 'red' };
      default:
        return { label: status, color: 'gray' };
    }
  }
}

// ============================================================================
// KYC SERVICE FACTORY
// ============================================================================

let kycServiceInstance: KYCService | null = null;

export function createKYCService(
  config: Partial<KYCServiceConfig>,
  fetchAdapter: (url: string, options?: RequestInit) => Promise<Response>,
  getAuthToken: () => Promise<string | null>
): KYCService {
  kycServiceInstance = new KYCService(config, fetchAdapter, getAuthToken);
  return kycServiceInstance;
}

export function getKYCService(): KYCService {
  if (!kycServiceInstance) {
    throw new Error('KYCService not initialized. Call createKYCService first.');
  }
  return kycServiceInstance;
}
